import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getExecucao } from '../services/api';
import { getChecklistUserName, setChecklistUserName } from '../utils/userStorage';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';

const AlertTriangle = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" x2="12" y1="9" y2="13" />
    <line x1="12" x2="12.01" y1="17" y2="17" />
  </svg>
);

const ArrowLeft = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

/* helpers */
const normalize = (val) => {
  if (val == null || val === '') return 'EM_BRANCO';
  const v = String(val).trim().toUpperCase();
  if (['CONFORME', 'SIM', 'OK', 'TRUE'].includes(v)) return 'CONFORME';
  if (['NAO_CONFORME', 'NÃO CONFORME', 'NÃO', 'NAO', 'FALSE'].includes(v)) return 'NAO_CONFORME';
  return v.includes('CONFORME') ? 'CONFORME' : 'NAO_CONFORME';
};
const pct = (num, den) => (den ? ((num / den) * 100) : 0);
const COLORS = {
  conforme: '#16a34a', naoConforme: '#ef4444', grid: '#e5e7eb'
};
const Badge = ({ children, tone = 'slate' }) => {
  const map = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${map[tone] || map.slate}`}>{children}</span>;
};
const Card = ({ title, value, subtitle }) => (
  <div className="rounded-2xl bg-white shadow-sm p-5 border border-gray-100">
    <div className="text-sm text-gray-500">{title}</div>
    <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
  </div>
);

const ExecucaoDetalhesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [execucao, setExecucao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuthSession();
  const [currentUserName, setCurrentUserName] = useState(getChecklistUserName() || 'Usuário');

  useEffect(() => {
    const storedName = getChecklistUserName();
    if (storedName) {
      setCurrentUserName(storedName);
      return;
    }

    const rawName = user?.username || user?.name;
    const normalizedName = typeof rawName === 'string' ? rawName.trim() : String(rawName || '').trim();
    if (normalizedName) {
      setCurrentUserName(normalizedName);
      setChecklistUserName(normalizedName);
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getExecucao(id);
        setExecucao(data);
      } catch (err) {
        setError(err?.message || 'Erro inesperado ao carregar.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const secoes = useMemo(
    () => execucao?.checklist_modelos?.checklist_secoes_modelo ?? [],
    [execucao]
  );

  const respostas = useMemo(
    () => execucao?.respostas ?? [],
    [execucao]
  );

  const totalPerguntas = useMemo(
    () => secoes.reduce((acc, s) => acc + (s.checklist_perguntas_modelo?.length || 0), 0),
    [secoes]
  );
  const perguntaById = useMemo(() => {
    const map = new Map();
    secoes.forEach(sec =>
      (sec.checklist_perguntas_modelo || []).forEach(p =>
        map.set(p.pergunta_id, { ...p, secaoTitulo: sec.titulo })
      )
    );
    return map;
  }, [secoes]);

  const dist = useMemo(() => {
    const counts = { CONFORME: 0, NAO_CONFORME: 0, EM_BRANCO: 0 };
    respostas.forEach(r => { counts[normalize(r.valor_resposta)] += 1; });
    const naoRespondidas = Math.max(totalPerguntas - respostas.length, 0);
    counts.EM_BRANCO += naoRespondidas;
    return counts;
  }, [respostas, totalPerguntas]);

  const totalConforme = dist.CONFORME;
  const totalNaoConforme = dist.NAO_CONFORME;
  const pontuacao = useMemo(
    () => pct(totalConforme, totalPerguntas).toFixed(2),
    [totalConforme, totalPerguntas]
  );

  const resultadoSecoes = useMemo(() => {
    return secoes.map(sec => {
      const perguntas = sec.checklist_perguntas_modelo || [];
      const ids = new Set(perguntas.map(p => p.pergunta_id));
      const respostasSecao = respostas.filter(r => ids.has(r.pergunta_id));
      const conformes = respostasSecao.filter(r => normalize(r.valor_resposta) === 'CONFORME').length;
      const naoConformes = respostasSecao.filter(r => normalize(r.valor_resposta) === 'NAO_CONFORME').length;
      const respondidas = respostasSecao.length;
      const blanks = Math.max(perguntas.length - respondidas, 0);

      return {
        titulo: sec.titulo,
        total: perguntas.length,
        conformes,
        naoConformes,
        emBranco: blanks,
        percent: perguntas.length ? pct(conformes, perguntas.length).toFixed(2) : '0.00'
      };
    });
  }, [secoes, respostas]);

  const naoConformidadesList = useMemo(() => {
    return respostas
      .filter(r => normalize(r.valor_resposta) === 'NAO_CONFORME')
      .map(r => {
        const p = perguntaById.get(r.pergunta_id);
        return {
          perguntaId: r.pergunta_id,
          pergunta: p?.texto_pergunta || `Pergunta #${r.pergunta_id}`,
          secao: p?.secaoTitulo || '-',
          comentario: (r.comentarios ?? '').trim()
        };
      });
  }, [respostas, perguntaById]);

  const dataPizza = useMemo(() => ([
    { name: 'Conforme', value: totalConforme, key: 'CONFORME', color: COLORS.conforme },
    { name: 'Não Conforme', value: totalNaoConforme, key: 'NAO_CONFORME', color: COLORS.naoConforme },
  ]), [totalConforme, totalNaoConforme]);

  const dataBarrasResumo = useMemo(() => ([
    { name: 'Conforme', value: totalConforme },
    { name: 'Não Conforme', value: totalNaoConforme },
  ]), [totalConforme, totalNaoConforme]);

  const dataBarrasSecoes = useMemo(() => {
    const base = resultadoSecoes.map(s => ({
      name: s.titulo,
      Conforme: s.conformes,
      'Não Conforme': s.naoConformes,
      total: s.total
    }));
    base.sort((a, b) => (b['Não Conforme'] - a['Não Conforme']) || (b.total - a.total));
    return base;
  }, [resultadoSecoes]);

  const dataRankingSecao = useMemo(() => {
    const acc = {};
    naoConformidadesList.forEach(item => {
      acc[item.secao] = (acc[item.secao] || 0) + 1;
    });
    return Object.entries(acc).map(([secao, count]) => ({ name: secao, value: count }));
  }, [naoConformidadesList]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="h-2 w-40 rounded bg-purple-200 animate-pulse mb-4" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-white shadow-sm border border-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800">Erro ao Carregar</h2>
        <p className="text-gray-600 mt-2">{error}</p>
      </div>
    );
  }
  if (!execucao) return <div className="p-6 text-center">Execução não encontrada.</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-md text-sm shadow-sm"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6 md:p-7">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              {execucao.checklist_modelos?.titulo}
            </h1>
            <p className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-1">
              <span>{new Date(execucao.concluido_em || execucao.iniciado_em).toLocaleString('pt-BR')}</span>
              <span className="text-gray-300">•</span>
              <span>
                Preenchido por <span className="font-medium text-gray-700">{currentUserName}</span>
              </span>
              <span className="text-gray-300">•</span>
              <span>{execucao.unidades?.nome}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={execucao.status === 'CONCLUIDO' ? 'green' : 'amber'}>{execucao.status}</Badge>
            <Badge tone="purple">Modelo #{execucao.modelo_id}</Badge>
            <Badge tone="slate">Execução #{execucao.execucao_id}</Badge>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          <Card title="Pontuação Final" value={`${pontuacao}%`} subtitle="% de perguntas em conformidade" />
          <Card title="Perguntas no modelo" value={totalPerguntas} />
          <Card title="Respondidas" value={respostas.length} subtitle={`${pct(respostas.length, totalPerguntas).toFixed(0)}% do total`} />
          <Card title="Conformes" value={totalConforme} subtitle={`${pct(totalConforme, totalPerguntas).toFixed(0)}%`} />
          <Card title="Não conformes" value={totalNaoConforme} subtitle={`${pct(totalNaoConforme, totalPerguntas).toFixed(0)}%`} />
        </div>
      </div>

      {/* Resumo + Pizza */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-lg text-gray-900">Resumo</h2>
          <div className="mt-3 text-sm text-gray-700 space-y-1">
            <div>Respondeu <strong>{respostas.length}</strong> de <strong>{totalPerguntas}</strong></div>
            <ul className="space-y-1">
              <li className="flex items-center justify-between">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-green-600" />Conforme</span>
                <span>{totalConforme}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Não conforme</span>
                <span>{totalNaoConforme}</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 h-[220px] sm:h-[240px] md:h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={dataPizza} dataKey="value" nameKey="name" outerRadius={90} innerRadius={48} paddingAngle={3}>
                  {dataPizza.map(d => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Legend verticalAlign="bottom" height={24} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-lg text-gray-900">Conformidade (barras)</h2>
          <div className="mt-4 h-[220px] sm:h-[240px] md:h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dataBarrasResumo}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  <Cell fill={COLORS.conforme} />
                  <Cell fill={COLORS.naoConforme} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Seções */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg text-gray-900">Resultado por Seção</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Seção</th>
                  <th className="px-4 py-2 text-center">Total</th>
                  <th className="px-4 py-2 text-center">Conforme</th>
                  <th className="px-4 py-2 text-center">Não conforme</th>
                  <th className="px-4 py-2 text-center">% Conf.</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {resultadoSecoes.map(sec => (
                  <tr key={sec.titulo} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{sec.titulo}</td>
                    <td className="px-4 py-2 text-center">{sec.total}</td>
                    <td className="px-4 py-2 text-center text-green-600 font-medium">{sec.conformes}</td>
                    <td className="px-4 py-2 text-center text-red-600 font-medium">{sec.naoConformes}</td>
                    <td className="px-4 py-2 text-center">{sec.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-gray-100 p-3">
            <div className="h-[280px] sm:h-[320px] md:h-[380px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dataBarrasSecoes} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={180} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Conforme" stackId="a" fill={COLORS.conforme} radius={[0, 6, 6, 0]}>
                    <LabelList dataKey="Conforme" position="insideLeft" fill="#fff" />
                  </Bar>
                  <Bar dataKey="Não Conforme" stackId="a" fill={COLORS.naoConforme} radius={[6, 0, 0, 6]}>
                    <LabelList dataKey="Não Conforme" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Ranking + detalhes */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-lg text-gray-900">Seções com mais não conformidades</h2>
          <div className="mt-4 h-[220px] sm:h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[...dataRankingSecao].sort((a, b) => b.value - a.value)}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill={COLORS.naoConforme} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="value" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-lg text-gray-900">Não conformidades (detalhes)</h2>
          {naoConformidadesList.length === 0 ? (
            <p className="text-sm text-gray-600 mt-3">Nenhuma não conformidade registrada.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {naoConformidadesList.map((item, idx) => (
                <div key={`${item.perguntaId}-${idx}`} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Badge tone="red">Não conforme</Badge>
                    <span>Seção: <span className="text-gray-700">{item.secao}</span></span>
                  </div>
                  <div className="mt-2 text-sm text-gray-900 font-medium">{item.pergunta}</div>
                  {item.comentario ? (
                    <div className="mt-1 text-sm text-gray-600"><span className="font-medium">Comentário: </span>{item.comentario}</div>
                  ) : <div className="mt-1 text-xs text-gray-400 italic">Sem comentário.</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecucaoDetalhesPage;
